import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ComponentProps } from 'react'

interface MarkdownRendererProps {
  content: string
  className?: string
  components?: ComponentProps<typeof ReactMarkdown>['components']
}

const MarkdownRenderer = ({ content, className, components }: MarkdownRendererProps) => {
  // Preprocess content to convert \n to markdown line breaks (two spaces + newline)
  const processedContent = content.replace(/\\n/g, '  \n')

  const defaultComponents: ComponentProps<typeof ReactMarkdown>['components'] = {
    // Customize paragraph styling to maintain consistent spacing
    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
    // Style links with proper colors and hover states
    a: ({ children, href }) => (
      <a
        href={href}
        className="text-blue-11 hover:text-blue-12 underline underline-offset-2"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    // Style code inline
    code: ({ children }) => (
      <code className="bg-gray-3 text-gray-12 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
    ),
    // Style strong/bold text
    strong: ({ children }) => <strong className="font-semibold text-gray-12">{children}</strong>,
    // Style emphasis/italic text
    em: ({ children }) => <em className="italic">{children}</em>,
    // Handle line breaks
    br: () => <br />,
    // Custom list styling - more compact for notifications
    ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 mt-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 mt-1">{children}</ol>,
    li: ({ children }) => <li className="text-inherit text-sm">{children}</li>,
    // Block quotes for important information
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-gray-6 pl-2 ml-2 my-1 text-gray-11 italic">{children}</blockquote>
    ),
    // Headings - scaled down for notification context
    h1: ({ children }) => <h1 className="text-sm font-semibold text-gray-12 mt-1 mb-0.5">{children}</h1>,
    h2: ({ children }) => <h2 className="text-sm font-semibold text-gray-12 mt-1 mb-0.5">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-medium text-gray-12 mt-1 mb-0.5">{children}</h3>,
    // GFM: Strikethrough
    del: ({ children }) => <del className="line-through text-gray-10">{children}</del>,
    // GFM: Tables - simplified for notification context
    table: ({ children }) => (
      <table className="min-w-full border-collapse border border-gray-6 text-xs mt-1 mb-1">{children}</table>
    ),
    thead: ({ children }) => <thead className="bg-gray-2">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-gray-6">{children}</tr>,
    th: ({ children }) => (
      <th className="border border-gray-6 px-2 py-1 text-left font-medium text-gray-12">{children}</th>
    ),
    td: ({ children }) => <td className="border border-gray-6 px-2 py-1 text-gray-11">{children}</td>,
    ...components
  }

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={defaultComponents}
        // Disable potentially unsafe features
        disallowedElements={['script', 'iframe', 'object', 'embed']}
        unwrapDisallowed={true}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownRenderer
